'use client'

import { useCallback, useState, type MouseEvent, type ReactNode } from 'react'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type { SxProps, Theme } from '@mui/material/styles'
import { panoyaYaz } from '@/lib/karekod/karekodPanosu'
import { gs1HamKarekodMu } from '@/utils/dataMatrixParser'
import Gs1HamKarekodMetni from '@/components/common/Gs1HamKarekodMetni'

export type CopyableBarkodProps = {
  value?: string | null
  /** Kopyalanmayan önek (ör. `GTIN: `, `Barkod `). */
  prefix?: ReactNode
  /** Boş / geçersiz değerde gösterilecek içerik. Varsayılan: `—` */
  empty?: ReactNode
  /** Boş değerde hiçbir şey render etme. */
  hideWhenEmpty?: boolean
  sx?: SxProps<Theme>
  className?: string
  /**
   * Görünür metin `value` dışında olmalıysa (ör. normalize edilmiş GTIN).
   * Panoya her zaman `value` (veya `copyValue`) yazılır.
   */
  displayValue?: ReactNode
  /** Panoya yazılacak metin; verilmezse `value` kullanılır. */
  copyValue?: string | null
  /** Tooltip; varsayılan «Barkodu kopyala». */
  copyLabel?: string
}

function normalizeBarkod(value?: string | null): string | null {
  const trimmed = (value ?? '').trim()
  if (!trimmed || trimmed === '—' || trimmed === '-') return null
  return trimmed
}

/**
 * Salt okunur barkod / GTIN metni. Tıklanınca panoya kopyalar;
 * satır seçimi / sağ tık menüsü gibi üst etkileşimleri `stopPropagation` ile korur.
 */
export default function CopyableBarkod({
  value,
  prefix,
  empty = '—',
  hideWhenEmpty = false,
  sx,
  className,
  displayValue,
  copyValue,
  copyLabel = 'Barkodu kopyala',
}: CopyableBarkodProps) {
  const barkod = normalizeBarkod(copyValue ?? value)
  const [kopyalandi, setKopyalandi] = useState(false)

  const handleClick = useCallback(
    async (event: MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()
      if (!barkod) return
      const yazildi = await panoyaYaz(barkod)
      if (!yazildi) return
      setKopyalandi(true)
      window.setTimeout(() => setKopyalandi(false), 1600)
    },
    [barkod],
  )

  if (!barkod) {
    if (hideWhenEmpty) return null
    return (
      <Typography component="span" className={className} sx={sx}>
        {empty}
      </Typography>
    )
  }

  return (
    <Tooltip title={kopyalandi ? 'Kopyalandı' : copyLabel} enterDelay={350}>
      <Typography
        component="span"
        className={className}
        onClick={(event) => void handleClick(event)}
        sx={{
          cursor: 'copy',
          userSelect: 'text',
          borderRadius: 0.5,
          transition: 'background-color 0.12s ease',
          '&:hover': {
            bgcolor: (theme) => theme.palette.action.hover,
          },
          ...sx,
        }}
      >
        {prefix}
        {displayValue ?? (gs1HamKarekodMu(barkod) ? <Gs1HamKarekodMetni ham={barkod} /> : barkod)}
      </Typography>
    </Tooltip>
  )
}
